import classNames from 'classnames';
import omit from 'lodash.omit';
import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDrag } from 'react-dnd';
import { DragTypes } from '../../../../lib/constants';

import {
  AnalyticsComponent,
  AnalyticsPage,
  trackProviderComponentViewedEvent,
} from '../../../../../lib/analytics';

import { ProviderActivityType } from '../../../../types/activity';

import { ProviderActivityObject } from '../../../../lib/providerActivity';
import {
  isAssessmentAForm,
  isStandardAssessment,
} from '../../../../lib/assessments';

import ActionButton from '../../../ActionButton';
import ActionButtonGroup from '../../../ActionButtonGroup';

import {
  DotsHorizontalIcon,
  MailIcon,
  EyeIcon,
  ClipboardListIcon,
  PencilAltIcon,
} from '@heroicons/react/outline';
import { ContextMenuPosition } from '../../../../types/contextMenu';
import { ProviderCollection } from '../../../../types/providerCollection';
import ActivityDateAdded from './ActivityDateAdded';
import ActivitySummary from './ActivitySummary';
import { ActivityListDataFragment } from '../../../../../generated/graphql';

const LibraryTableRow: FC<{
  providerActivity: ActivityListDataFragment;
  setSelectedProviderActivity: React.Dispatch<
    React.SetStateAction<ActivityListDataFragment | undefined>
  >;
  setIsProviderActivitySlideoverOpen?: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  openAddOrEditTextModal?: () => void;
  followUpMode?: boolean;
  restrictSingleSelection?: boolean;
  isLastProviderActivity?: boolean;
  setSelectedProviderActivitiesObject?: React.Dispatch<
    React.SetStateAction<ProviderActivityObject>
  >;
  selectedProviderActivitiesObject?: ProviderActivityObject;
  isChecked?: boolean;
  providerActivityId?: string;
  showActivityActionsMenu?: boolean;
  setShowActivityActionsMenu?: React.Dispatch<React.SetStateAction<boolean>>;
  setActivityActionsMenuPosition: React.Dispatch<
    React.SetStateAction<ContextMenuPosition>
  >;
  sendProviderActivitiesInFollowUp?: (
    providerActivities: ActivityListDataFragment[],
  ) => void;
  canDragIntoCollection?: boolean;
  onCheckboxClick?: (event: React.MouseEvent<HTMLInputElement>) => void;
  selectedProviderCollection?: ProviderCollection;
}> = ({
  providerActivity,
  setSelectedProviderActivity,
  setIsProviderActivitySlideoverOpen,
  openAddOrEditTextModal,
  followUpMode = false,
  restrictSingleSelection = false,
  isLastProviderActivity,
  setSelectedProviderActivitiesObject,
  selectedProviderActivitiesObject,
  isChecked = false,
  providerActivityId = '',
  showActivityActionsMenu,
  setShowActivityActionsMenu,
  setActivityActionsMenuPosition,
  sendProviderActivitiesInFollowUp,
  canDragIntoCollection = false,
  onCheckboxClick,
  selectedProviderCollection,
}) => {
  const onClickResourceRow = () => {
    if (!showActivityActionsMenu) {
      setSelectedProviderActivity(providerActivity);
      if (providerActivity.__typename === ProviderActivityType.Text) {
        openAddOrEditTextModal?.();
      } else {
        setIsProviderActivitySlideoverOpen?.(true);
        trackProviderComponentViewedEvent(
          AnalyticsComponent.ActivitySlideover,
          AnalyticsPage.Library,
          {
            activityType: providerActivity.activityType,
          },
        );
      }
    }
  };

  const handleCheckboxInteraction = () => {
    if (!selectedProviderActivitiesObject?.[providerActivityId]) {
      if (restrictSingleSelection) {
        // Clear the object and set the selected provider activity to ensure only 1 selection
        // at a time
        setSelectedProviderActivitiesObject?.({
          [providerActivityId]: providerActivity,
        });
      } else {
        setSelectedProviderActivitiesObject?.({
          ...selectedProviderActivitiesObject,
          ...{ [providerActivityId]: providerActivity },
        });
      }
    } else {
      const remainingSelectedProviderActivities = omit(
        selectedProviderActivitiesObject,
        [providerActivityId],
      );

      setSelectedProviderActivitiesObject?.(
        remainingSelectedProviderActivities,
      );
    }
  };

  const navigate = useNavigate();

  const isPractitionerForm =
    providerActivity.__typename === 'ActivityAssessment' &&
    providerActivity.assessment?.completableByProviderOnly === true;
  const assessmentId =
    providerActivity.__typename === 'ActivityAssessment'
      ? providerActivity.assessment?.id
      : undefined;
  const isCustomForm =
    providerActivity.__typename === 'ActivityAssessment' &&
    isAssessmentAForm(providerActivity.assessment);

  const handleEditForm = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!assessmentId) return;
    if (selectedProviderCollection?.id) {
      navigate(
        `/library/forms/${assessmentId}?collection=${selectedProviderCollection.id}`,
      );
    } else {
      navigate(`/library/forms/${assessmentId}`, {
        state: { fromPage: 'library' },
      });
    }
  };

  const [{ isDragging }, drag] = useDrag({
    type: DragTypes.LIBRARY_ITEM,
    item: () => ({
      providerActivity,
      id: providerActivity.id,
    }),
    canDrag: () => canDragIntoCollection,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  return (
    <tr
      ref={drag}
      style={{ opacity: isDragging ? 0.5 : 1 }}
      className={classNames(
        'max-h-[48px] cursor-pointer text-ellipsis text-caption text-secondary-150 hover:bg-neutral-25',
        !isLastProviderActivity && 'border-b border-b-neutral-75',
        isDragging && 'cursor-move',
        !canDragIntoCollection && 'cursor-default',
      )}
      onClick={() => {
        if (!followUpMode) {
          onClickResourceRow();
        } else {
          handleCheckboxInteraction();
        }
      }}
      onContextMenu={(e) => {
        // Prevent the browser context menu from showing when right clicked
        e.preventDefault();
        if (!followUpMode && !isStandardAssessment(providerActivity)) {
          setSelectedProviderActivity(providerActivity);
          setActivityActionsMenuPosition({
            x: e.pageX,
            y: e.pageY,
          });
          setShowActivityActionsMenu?.(true);
        }
      }}
    >
      <td
        className="py-0 pl-6 pr-4 sm:w-16 sm:px-8"
        onClick={(event) => {
          // Prevent the click from bubbling up to the row.
          event.stopPropagation();
        }}
      >
        {isChecked && <div className="inset-y-0 left-0 w-0.5 bg-white" />}
        <input
          type="checkbox"
          checked={isChecked}
          className="h-5 w-5 cursor-pointer rounded border-neutral-100 text-secondary-125 focus:ring-2 focus:ring-secondary-125"
          onChange={(e) => {
            onCheckboxClick?.(
              e.nativeEvent as unknown as React.MouseEvent<HTMLInputElement>,
            );
          }}
        />
      </td>
      <td className={classNames(!followUpMode ? 'w-2/3' : 'w-1/2')}>
        <ActivitySummary providerActivity={providerActivity} followUpMode />
      </td>

      <td className="w-1/3">
        <ActivityDateAdded providerActivity={providerActivity} />
      </td>
      {followUpMode ? (
        <td className="min-w-[120px]">
          <div className="-mt-px flex h-12 items-center justify-end pr-6">
            <ActionButtonGroup size="small">
              <ActionButton
                title="Preview"
                onClick={(e) => {
                  onClickResourceRow();
                  e.stopPropagation();
                }}
              >
                <EyeIcon className="h-5 w-5 text-neutral-125" />
              </ActionButton>
            </ActionButtonGroup>
          </div>
        </td>
      ) : (
        <td className="min-w-[120px]">
          <div className="-mt-px flex h-12 items-center justify-end pr-6">
            <ActionButtonGroup size="small">
              {isCustomForm && assessmentId && (
                <ActionButton
                  onClick={handleEditForm}
                  title="Edit form"
                  aria-label="Edit form"
                >
                  <PencilAltIcon className="h-5 w-5 text-neutral-125" />
                </ActionButton>
              )}
              {isPractitionerForm && assessmentId && (
                <ActionButton
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(
                      `/complete-practitioner-form?assessmentId=${assessmentId}`,
                    );
                  }}
                  title="Complete for client"
                >
                  <ClipboardListIcon className="h-5 w-5 text-neutral-125" />
                </ActionButton>
              )}
              {sendProviderActivitiesInFollowUp && !isPractitionerForm && (
                <ActionButton
                  onClick={(e) => {
                    e.stopPropagation();
                    sendProviderActivitiesInFollowUp?.([providerActivity]);
                  }}
                  title="Send to clients"
                >
                  <MailIcon className="h-5 w-5 text-neutral-125" />
                </ActionButton>
              )}
              {!followUpMode && !isStandardAssessment(providerActivity) && (
                <ActionButton
                  onClick={(e) => {
                    setSelectedProviderActivity(providerActivity);
                    setShowActivityActionsMenu?.(true);
                    if (!followUpMode) {
                      setActivityActionsMenuPosition({
                        x: e.pageX,
                        y: e.pageY,
                      });
                    }
                    e.stopPropagation();
                  }}
                  title="More actions"
                >
                  <DotsHorizontalIcon className="h-5 w-5 text-neutral-125" />
                </ActionButton>
              )}
            </ActionButtonGroup>
          </div>
        </td>
      )}
    </tr>
  );
};

export default LibraryTableRow;
